import React, { useRef, useEffect } from 'react';
import type { ProjectDetailStageProps } from '../projects.types';
import { ProjectMiniRail } from './ProjectMiniRail';
import { ProjectCarouselCanvas } from './ProjectCarouselCanvas';
import './ProjectDetailStage.styles.css';

/**
 * Master-detail expanded stage view.
 * Integrates left sibling rail (desktop), Decoy Shield carousel, and structured metadata.
 */
export const ProjectDetailStage: React.FC<ProjectDetailStageProps> = ({
  project,
  siblingProjects,
  isMobile,
  onSelectSibling,
  onClose,
}) => {
  const cardRef = useRef<HTMLElement | null>(null);

  // * Support Escape key to close expanded detail stage
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // * Isolate wheel and touch events so internal details scrolling does not trigger sequence slide changes
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const handleScrollIsolation = (e: Event) => {
      e.stopPropagation();
    };

    el.addEventListener('wheel', handleScrollIsolation, { passive: false });
    el.addEventListener('touchmove', handleScrollIsolation, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleScrollIsolation);
      el.removeEventListener('touchmove', handleScrollIsolation);
    };
  }, []);

  return (
    <div className="detail-stage-wrapper">
      {/* * Left-side vertical rail of shrunken sibling cards (Desktop only) */}
      {!isMobile && siblingProjects.length > 0 && (
        <ProjectMiniRail
          projects={siblingProjects}
          activeProjectId={project.id}
          onSelect={onSelectSibling}
        />
      )}

      {/* * Main Expanded Detail Card with Orange Border Accent */}
      <article
        ref={cardRef}
        className="detail-stage-card"
        aria-label={`Details for ${project.title}`}
      >
        {/* * Floating Top-Right Close Button Positioned Above Card */}
        <button
          type="button"
          className="detail-close-btn"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close Project Details"
        >
          <i className="fas fa-times" />
          <span className="detail-close-label">Close</span>
        </button>

        {/* * Left Half: In-Memory Canvas Staging Carousel with Decoy Shield */}
        <div className="detail-stage-left">
          <ProjectCarouselCanvas
            images={
              project.images && project.images.length > 0
                ? project.images
                : [project.image || '']
            }
            title={project.title}
          />
        </div>

        {/* * Right Half: Categorized Project Metadata */}
        <div className="detail-stage-right">
          <header className="detail-header">
            <span className="detail-category-badge">{project.category}</span>
            <h3 className="detail-title">{project.title}</h3>
          </header>

          {/* * Role Section */}
          {project.role && (
            <div className="detail-section-block">
              <span className="detail-section-label">
                <i className="fas fa-user-tag" /> Role
              </span>
              <p className="detail-section-text">{project.role}</p>
            </div>
          )}

          {/* * Project Objective */}
          {project.objective && (
            <div className="detail-section-block">
              <span className="detail-section-label">
                <i className="fas fa-bullseye" /> Project Objective
              </span>
              <p className="detail-section-text">{project.objective}</p>
            </div>
          )}

          {/* * Project Description */}
          <div className="detail-section-block">
            <span className="detail-section-label">
              <i className="fas fa-align-left" /> Project Description
            </span>
            <p className="detail-section-text">
              {project.detailedDescription || project.description}
            </p>
          </div>

          {/* * Technologies Used with [ <tag> ] styling */}
          <div className="detail-section-block">
            <span className="detail-section-label">
              <i className="fas fa-code" /> Technologies Used
            </span>
            <div className="detail-tech-list">
              {project.tech.map((tech, idx) => (
                <span key={idx} className="bracket-tech-tag">
                  <span className="bracket-symbol">[</span>
                  <span>{tech}</span>
                  <span className="bracket-symbol">]</span>
                </span>
              ))}
            </div>
          </div>

          {/* * Achievements / Awards */}
          {project.achievement && (
            <div className="detail-achievement-box">
              <i className="fas fa-trophy" />
              <span>{project.achievement}</span>
            </div>
          )}

          {/* * Action Links */}
          <div className="detail-actions">
            {project.liveDemo && project.liveDemo !== '#' && (
              <a
                href={project.liveDemo}
                target="_blank"
                rel="noopener noreferrer"
                className="detail-action-link primary"
              >
                <i className="fas fa-external-link-alt" />
                Live Demo
              </a>
            )}
            {project.githubLink && project.githubLink !== '#' && (
              <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="detail-action-link secondary"
              >
                <i className="fab fa-github" />
                View Code
              </a>
            )}
          </div>
        </div>
      </article>
    </div>
  );
};

export default ProjectDetailStage;
